import { useState, Fragment } from 'react';
import { EVOpportunity } from '../types';
import BookmakerLogo from './BookmakerLogo';
import { formatOdds as formatOddsUtil, type OddsFormat } from '../utils/oddsFormat';
import { getSafeExternalUrl } from '../utils/url';

interface EVTableProps {
  opportunities: EVOpportunity[];
  oddsFormat: OddsFormat;
  userTier?: 'free' | 'gold' | 'platinum';
}

export default function EVTable({ opportunities, oddsFormat, userTier = 'free' }: EVTableProps) {
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 0) {
      return 'LIVE';
    } else if (diffMins < 60) {
      return `${diffMins}m`;
    } else if (diffMins < 1440) {
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
    } else {
      return date.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  const formatOdds = (odds: number) => {
    // The API always returns decimal odds, so we convert to the selected format for display
    return formatOddsUtil(odds, oddsFormat, 'decimal');
  };

  const isLive = (dateString: string) => {
    return new Date(dateString) < new Date();
  };

  const toggleRow = (opportunityId: string) => {
    setExpandedRowId(expandedRowId === opportunityId ? null : opportunityId);
  };

  const formatMarket = (opportunity: EVOpportunity) => {
    const marketKey = opportunity.market_key || 'h2h';
    const point = opportunity.point;
    const outcomeName = opportunity.outcome_name;

    switch (marketKey) {
      case 'h2h':
        return {
          type: 'Moneyline',
          detail: outcomeName
        };
      case 'spreads':
        const spreadSign = point !== undefined && point > 0 ? '+' : '';
        return {
          type: 'Spread',
          detail: `${outcomeName} ${spreadSign}${point}`
        };
      case 'totals':
        return {
          type: 'Total',
          detail: `${outcomeName} ${point}`
        };
      default:
        // For player props or other markets
        return {
          type: marketKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          detail: outcomeName
        };
    }
  };

  return (
    <div className="opportunities-wrapper">
      <table className="opportunities-table">
        <thead>
          <tr>
            <th></th>
            <th>+EV %</th>
            <th>EVENT</th>
            <th>MARKET</th>
            <th>BOOKS & BETS</th>
            <th>PROBABILITY</th>
          </tr>
        </thead>
        <tbody>
          {opportunities.map((opportunity) => {
            const isExpanded = expandedRowId === opportunity.id;
            const safeBetLink = getSafeExternalUrl(opportunity.link);

            return (
              <Fragment key={opportunity.id}>
                <tr
                  onClick={() => toggleRow(opportunity.id)}
                  style={{ cursor: 'pointer' }}
                  className={isExpanded ? 'expanded-row' : ''}
                >
                  {/* Expand Indicator */}
                  <td style={{ width: '30px', textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-block',
                      transition: 'transform 0.2s',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
                    }}>
                      ▶
                    </span>
                  </td>

                  {/* +EV % */}
                  <td className="table-cell-profit">
                    +{opportunity.ev_percentage.toFixed(2)}%
                  </td>

                {/* EVENT */}
                <td>
                  {/* Date/Time Line */}
                  <div className="table-cell-meta" style={{ marginBottom: '4px' }}>
                    {isLive(opportunity.commence_time) ? (
                      <span style={{ color: '#4da3ff', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{
                          display: 'inline-block',
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: '#4da3ff'
                        }}></span>
                        LIVE
                      </span>
                    ) : (
                      formatDate(opportunity.commence_time)
                    )}
                  </div>
                  {/* Teams Line */}
                  <div className="table-cell-event" style={{ marginBottom: '4px' }}>
                    {opportunity.away_team} vs {opportunity.home_team}
                  </div>
                  {/* Sport Line */}
                  <div className="table-cell-event">
                    {opportunity.sport_title || opportunity.sport_key}
                  </div>
                </td>

                {/* MARKET */}
                <td>
                  {(() => {
                    const market = formatMarket(opportunity);
                    return (
                      <>
                        <div className="table-cell-meta" style={{ fontSize: '10px', color: 'var(--text-secondary)', marginBottom: '2px' }}>
                          {market.type}
                        </div>
                        <div className="table-cell-meta" style={{ fontSize: '11px' }}>
                          {market.detail}
                        </div>
                      </>
                    );
                  })()}
                </td>

                {/* BOOKS */}
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* Bookmaker */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                        <BookmakerLogo
                          bookmakerKey={opportunity.bookmaker_key}
                          bookmakerTitle={opportunity.bookmaker_title}
                        />
                        <span className="table-cell-odds" style={{ minWidth: '50px' }}>
                          {formatOdds(opportunity.bookmaker_odds)}
                        </span>
                      </div>
                      {safeBetLink ? (
                        userTier === 'free' ? (
                          <a
                            href="/account/billing"
                            className="table-bet-action-inline locked"
                            onClick={(e) => e.stopPropagation()}
                            title="Upgrade to Gold or Platinum for 1-click betting"
                          >
                            🔒
                          </a>
                        ) : (
                          <a
                            href={safeBetLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="table-bet-action-inline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Bet
                          </a>
                        )
                      ) : (
                        <span className="table-bet-action-inline disabled">N/A</span>
                      )}
                    </div>
                    {/* Sharp Line */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingLeft: '8px', borderLeft: '2px solid rgba(168, 178, 209, 0.2)' }}>
                      <span className="table-cell-meta" style={{ fontSize: '10px', minWidth: '65px', fontWeight: 600, color: 'rgba(168, 178, 209, 0.8)' }}>
                        Sharp Line
                      </span>
                      <span className="table-cell-odds" style={{ minWidth: '50px' }}>
                        {formatOdds(opportunity.sharp_odds)}
                      </span>
                      <span className="table-cell-meta" style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>
                        ({opportunity.sharp_books_count} books)
                      </span>
                    </div>
                  </div>
                </td>

                {/* PROBABILITY */}
                  <td className="table-cell-odds">
                    {(opportunity.sharp_probability * 100).toFixed(2)}%
                  </td>
                </tr>

                {/* Expandable Row */}
                {isExpanded && opportunity.all_bookmaker_odds && (() => {
                  const bookmakerList = opportunity.all_bookmaker_odds;
                  const bestOdd = opportunity.bookmaker_odds;
                  const sharpOdd = opportunity.sharp_odds;

                  return (
                    <tr key={`${opportunity.id}-expanded`} className="expanded-content-row">
                      <td colSpan={6} style={{ padding: '0', backgroundColor: '#0a141d' }}>
                        <div className="odds-table-container">
                          <table className="odds-comparison-table">
                            <thead>
                              <tr className="odds-table-header-row">
                                <th className="odds-table-outcome-header"></th>
                                <th className="odds-table-best-header">Best Odds</th>
                                <th className="odds-table-avg-header">Sharp Line</th>
                                {bookmakerList.map(bookmaker => (
                                  <th key={bookmaker.bookmaker_key} className="odds-table-bookmaker-header">
                                    <div className="odds-table-bookmaker-logo">
                                      <BookmakerLogo
                                        bookmakerKey={bookmaker.bookmaker_key}
                                        bookmakerTitle={bookmaker.bookmaker_title}
                                        size="small"
                                      />
                                    </div>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="odds-table-outcome-row">
                                <td className="odds-table-outcome-name">
                                  {opportunity.outcome_name}
                                </td>
                                <td className="odds-table-best-cell">
                                  <button className="odds-button odds-button-best">
                                    {formatOdds(bestOdd)}
                                  </button>
                                </td>
                                <td className="odds-table-avg-cell">
                                  <button className="odds-button odds-button-avg">
                                    {formatOdds(sharpOdd)}
                                  </button>
                                  <div className="odds-consensus-info">
                                    {opportunity.sharp_books_count} books
                                  </div>
                                </td>
                                {bookmakerList.map(bookmaker => {
                                  const isAboveSharp = bookmaker.odds > sharpOdd;
                                  const oddClass = isAboveSharp ? 'odds-button-above-avg' : 'odds-button-below-avg';
                                  const bookmakerLink = getSafeExternalUrl(bookmaker.link);

                                  return (
                                    <td key={bookmaker.bookmaker_key} className="odds-table-bookmaker-cell">
                                      <div className="odds-cell-content">
                                        {bookmakerLink ? (
                                          <a
                                            href={bookmakerLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`odds-button ${oddClass}`}
                                          >
                                            {formatOdds(bookmaker.odds)}
                                          </a>
                                        ) : (
                                          <button className={`odds-button ${oddClass}`}>
                                            {formatOdds(bookmaker.odds)}
                                          </button>
                                        )}
                                        {bookmakerLink && (
                                          <a
                                            href={bookmakerLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="odds-link-button"
                                          >
                                            Bet
                                          </a>
                                        )}
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  );
                })()}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
